import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useGetAllCoursesByClusterQuery } from "../../../../graphql/generated.tsx";
import AdminLayout from "../../Layout/index.jsx";
import CourseDashboardCard from "./CourseDashboardCard.jsx";
import Loader from "../../../../components/Loader.jsx";

const CourseForStudent = () => {
  const { state } = useLocation();
  const { campusName, clusterId, studentId, email } = state || {};
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const { data, loading, error } = useGetAllCoursesByClusterQuery({
    variables: { campusName, clusterId },
    skip: !campusName || !clusterId,
  });

  const courses = data?.getAllCoursesByCluster || [];
  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  const handleCourseClick = (courseId) => {
    setSelectedCourseId((prev) => (prev === courseId ? null : courseId));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex min-h-[40vh] items-center justify-center p-6">
          <Loader />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <p className="p-6 text-red-600">Error: {error.message}</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <h1 className="mb-2 text-2xl font-bold">
          Courses Assigned to {email}
        </h1>
        <p className="mb-6 text-sm text-slate-500">
          Select a course to load progress details
        </p>

        {courses.length === 0 ? (
          <p className="text-center text-slate-500">
            No courses in this cluster
          </p>
        ) : (
          <>
            {/* Course cards — always same compact size in a row */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => {
                const isSelected = selectedCourseId === course.id;

                return (
                  <article
                    key={course.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCourseClick(course.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCourseClick(course.id);
                      }
                    }}
                    className={`min-h-[148px] cursor-pointer rounded-xl border bg-white p-5 shadow-sm transition-all duration-200 ${
                      isSelected
                        ? "border-[#2d5670] shadow-md ring-2 ring-[#2d5670]/20"
                        : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                    }`}
                  >
                    <h2
                      className={`mb-2 text-xl font-bold ${
                        isSelected ? "text-[#2d5670]" : "text-slate-900"
                      }`}
                    >
                      {course.name}
                    </h2>
                    {course.short_description ? (
                      <p className="line-clamp-2 text-sm text-gray-600">
                        {course.short_description}
                      </p>
                    ) : null}
                    {!isSelected ? (
                      <p className="mt-4 text-sm font-medium text-blue-600">
                        Click to view progress
                      </p>
                    ) : (
                      <p className="mt-4 text-sm font-medium text-[#2d5670]">
                        Viewing progress below
                      </p>
                    )}
                  </article>
                );
              })}
            </div>

            {/* Analytics panel — opens below the card row */}
            {selectedCourse ? (
              <section
                className="mt-6 rounded-xl border border-[#2d5670]/30 bg-white p-6 shadow-md ring-1 ring-[#2d5670]/10 lg:p-8"
                aria-label={`Progress for ${selectedCourse.name}`}
              >
                <header className="mb-6 border-b border-slate-100 pb-5">
                  <h2 className="text-xl font-bold text-slate-900">
                    {selectedCourse.name}
                  </h2>
                  {selectedCourse.short_description ? (
                    <p className="mt-2 max-w-3xl text-sm leading-relaxed text-gray-600">
                      {selectedCourse.short_description}
                    </p>
                  ) : null}
                </header>

                <CourseDashboardCard
                  courseId={selectedCourse.id}
                  studentId={studentId}
                  campusName={campusName}
                />
              </section>
            ) : null}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default CourseForStudent;
